import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { cacheManager, queryKeys } from './queryClient';

// Types for API responses
export interface ApiResponse<T = any> {
  results?: T[];
  data?: T[];
  count?: number;
  total?: number;
  totalCount?: number;
  total_count?: number;
  totalRecords?: number;
  items?: T[];
  records?: T[];
  list?: T[];
}

export interface ExtractedData<T = any> {
  data: T[];
  total: number;
}

// Hook to get authenticated axios instance
export const useAuthenticatedAxios = () => {
  const { getIdTokenClaims } = useAuth0();

  const createAuthenticatedRequest = async (
    url: string,
    params?: Record<string, any>,
    config?: any
  ) => {
    const getidToken = await getIdTokenClaims();
    const idToken = getidToken?.__raw;

    return axios.get(url, {
      params,
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
        ...config?.headers,
      },
      ...config,
    });
  };

  return { createAuthenticatedRequest };
};

// Utility function to extract data from different API response structures
export const extractDataFromResponse = <T = any>(
  response: ApiResponse<T>
): ExtractedData<T> => {
  if (!response) return { data: [], total: 0 };

  // Handle different API response structures
  if (response.results && response.count !== undefined) {
    // Structure: { results: [...], count: number }
    return { data: response.results, total: response.count };
  } else if (response.data && response.total !== undefined) {
    // Structure: { data: [...], total: number }
    return { data: response.data, total: response.total };
  } else if (Array.isArray(response)) {
    // Structure: [...] (array of items)
    return { data: response, total: response.length };
  } else if (typeof response === 'object') {
    // Try to find data array and total count in response object
    const possibleDataKeys = ['items', 'records', 'list', 'data', 'results'];
    const possibleTotalKeys = [
      'total',
      'totalCount',
      'count',
      'total_count',
      'totalRecords',
    ];

    const dataKey = possibleDataKeys.find(key =>
      Array.isArray((response as any)[key])
    );
    const totalKey = possibleTotalKeys.find(
      key => typeof (response as any)[key] === 'number'
    );

    if (dataKey) {
      return {
        data: (response as any)[dataKey],
        total: totalKey
          ? (response as any)[totalKey]
          : (response as any)[dataKey].length,
      };
    }
  }

  // Fallback
  console.warn('Unexpected API response structure:', response);
  return { data: [], total: 0 };
};

// Generic hook for table data fetching
export const useTableData = <T = any>(
  apiUrl: string,
  params: Record<string, any>,
  options?: {
    enabled?: boolean;
    staleTime?: number;
    refetchInterval?: number;
  }
) => {
  const { isAuthenticated } = useAuth0();
  const { createAuthenticatedRequest } = useAuthenticatedAxios();

  return useQuery({
    queryKey: queryKeys.tableData(apiUrl, params),
    queryFn: cacheManager.createAuthAwareQueryFn(
      async (): Promise<ExtractedData<T>> => {
        try {
          const response = await createAuthenticatedRequest(apiUrl, params);
          return extractDataFromResponse<T>(response.data);
        } catch (error) {
          console.error('Error fetching table data:', error);
          throw error;
        }
      },
      isAuthenticated
    ),
    enabled: isAuthenticated && options?.enabled !== false,
    staleTime: options?.staleTime,
    refetchInterval: options?.refetchInterval,
  });
};

// Generic API hook for any endpoint
export const useApiData = <T = any>(
  endpoint: string,
  params?: Record<string, any>,
  options?: {
    enabled?: boolean;
    staleTime?: number;
    select?: (data: any) => T;
  }
) => {
  const { isAuthenticated } = useAuth0();
  const { createAuthenticatedRequest } = useAuthenticatedAxios();

  return useQuery({
    queryKey: queryKeys.api(endpoint, params),
    queryFn: cacheManager.createAuthAwareQueryFn(async (): Promise<T> => {
      const response = await createAuthenticatedRequest(endpoint, params);
      return response.data;
    }, isAuthenticated),
    enabled: isAuthenticated && options?.enabled !== false,
    staleTime: options?.staleTime,
    select: options?.select,
  });
};

// Mutation hook for API calls (POST, PUT, DELETE)
export const useApiMutation = <TData = any, TVariables = any>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: {
    onSuccess?: (data: TData, variables: TVariables) => void;
    onError?: (error: AxiosError, variables: TVariables) => void;
    invalidateQueries?: string[][];
  }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      // Invalidate related queries
      if (options?.invalidateQueries) {
        options.invalidateQueries.forEach(queryKey => {
          queryClient.invalidateQueries({ queryKey });
        });
      }
      options?.onSuccess?.(data, variables);
    },
    onError: options?.onError,
  });
};

// Hook for invalidating specific queries
export const useInvalidateQueries = () => {
  const queryClient = useQueryClient();

  const invalidateTableData = (apiUrl?: string) => {
    if (apiUrl) {
      queryClient.invalidateQueries({
        queryKey: ['tableData', apiUrl],
        exact: false,
      });
    } else {
      queryClient.invalidateQueries({
        queryKey: ['tableData'],
        exact: false,
      });
    }
  };

  const invalidateApi = (endpoint?: string) => {
    if (endpoint) {
      queryClient.invalidateQueries({
        queryKey: ['api', endpoint],
        exact: false,
      });
    } else {
      queryClient.invalidateQueries({
        queryKey: ['api'],
        exact: false,
      });
    }
  };

  const invalidateAll = () => {
    queryClient.invalidateQueries();
  };

  return {
    invalidateTableData,
    invalidateApi,
    invalidateAll,
  };
};

// Error handling utilities
export const isApiError = (error: unknown): error is AxiosError => {
  return axios.isAxiosError(error);
};

export const getErrorMessage = (error: unknown): string => {
  if (isApiError(error)) {
    // AxiosError extends Error, so it has a message property
    const axiosError = error as AxiosError;
    const responseData = axiosError.response?.data as any;
    return (
      responseData?.message || axiosError.message || 'An API error occurred'
    );
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unknown error occurred';
};
